import React, { useEffect, useState } from 'react';
import { db } from '../firebaseConfig';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import navbarConfig from '../navbarConfig.json';
import Modal from './Modal';
import { GripVertical } from 'lucide-react';

const DEFAULT_ITEMS = navbarConfig.navItems || [
  { text: 'O nás', href: '#about' },
  { text: 'Vozidla', href: '#vehicles' },
  { text: 'Členové', href: '#members' },
];

const NavMenuForm = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Modal: přidání nové položky
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newItem, setNewItem] = useState({ text: '', href: '' });
  const [newItemType, setNewItemType] = useState('anchor'); // 'anchor' | 'url'

  // Inline edit existující položky
  const [editingIndex, setEditingIndex] = useState(null);
  const [editingItem, setEditingItem] = useState({ text: '', href: '' });

  // Drag & drop
  const [dragIndex, setDragIndex] = useState(null);
  const [dropIndex, setDropIndex] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const ref = doc(db, 'content', 'site');
        const snap = await getDoc(ref);
        if (snap.exists()) {
          const data = snap.data();
          const loadedItems = Array.isArray(data.navItems) ? data.navItems : DEFAULT_ITEMS;
          setItems(loadedItems);
          if (!Array.isArray(data.navItems)) {
            await setDoc(ref, { navItems: DEFAULT_ITEMS }, { merge: true });
          }
        } else {
          await setDoc(ref, { navItems: DEFAULT_ITEMS }, { merge: true });
          setItems(DEFAULT_ITEMS);
        }
      } catch (err) {
        console.error('NavMenu: nepodařilo se načíst navItems', err);
        setError(err.message || 'Nepodařilo se načíst navigační položky');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Reorder helper
  const reorder = (list, startIndex, endIndex) => {
    const result = [...list];
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const onDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const onDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropIndex(index);
  };

  const onDrop = (e, index) => {
    e.preventDefault();
    const fromStr = e.dataTransfer.getData('text/plain');
    const from = dragIndex !== null ? dragIndex : (fromStr ? parseInt(fromStr, 10) : null);
    const to = index;
    if (from === null || to === null || from === to) {
      setDragIndex(null);
      setDropIndex(null);
      return;
    }
    setItems((prev) => reorder(prev, from, to));
    setDragIndex(null);
    setDropIndex(null);
  };

  const onDragEnd = () => {
    setDragIndex(null);
    setDropIndex(null);
  };

  const deleteItem = (index) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      setEditingIndex(null);
      setEditingItem({ text: '', href: '' });
    }
  };

  const startEdit = (index) => {
    setEditingIndex(index);
    setEditingItem(items[index]);
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditingItem({ text: '', href: '' });
  };

  const applyEdit = () => {
    if (!editingItem.text.trim() || !editingItem.href.trim()) return;
    setItems((prev) => prev.map((it, i) => (i === editingIndex ? editingItem : it)));
    setEditingIndex(null);
    setEditingItem({ text: '', href: '' });
  };

  const openAddModal = () => {
    setNewItem({ text: '', href: '' });
    setNewItemType('anchor');
    setIsAddModalOpen(true);
  };

  const addNewItem = () => {
    const text = newItem.text.trim();
    let href = newItem.href.trim();
    if (!text || !href) return;
    if (newItemType === 'anchor' && !href.startsWith('#')) {
      href = `#${href.replace(/^#/, '')}`;
    }
    setItems((prev) => [...prev, { text, href }]);
    setIsAddModalOpen(false);
  };

  const saveChanges = async () => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const ref = doc(db, 'content', 'site');
      await setDoc(ref, { navItems: items }, { merge: true });
      setSuccess(true);
    } catch (err) {
      console.error('NavMenu: nepodařilo se uložit navItems', err);
      setError(err.message || 'Nepodařilo se uložit navigační položky');
    } finally {
      setLoading(false);
    }
  };

  const setAnchor = (anchor) => {
    setNewItem((prev) => ({ ...prev, href: anchor }));
    setNewItemType('anchor');
  };

  if (loading && !items.length) {
    return <div>Načítám navigační položky…</div>;
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="text-red-600 text-sm">{error}</div>
      )}
      {success && (
        <div className="text-green-600 text-sm">Změny byly uloženy.</div>
      )}

      <div className="flex items-center justify-between">
        <h3 className="text-base font-semibold">Položky v navigaci</h3>
        <button onClick={openAddModal} className="inline-flex items-center gap-2 bg-indigo-600 text-white px-3 py-2 rounded-md hover:bg-indigo-700">
          Přidat položku
        </button>
      </div>

      <ul className="divide-y">
        {items.map((item, index) => (
          <li
            key={index}
            className={`py-3 flex items-center justify-between ${dropIndex === index ? 'ring-2 ring-indigo-300 rounded-md' : ''}`}
            draggable
            onDragStart={(e) => onDragStart(e, index)}
            onDragOver={(e) => onDragOver(e, index)}
            onDrop={(e) => onDrop(e, index)}
            onDragEnd={onDragEnd}
          >
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-gray-400 cursor-grab select-none"><GripVertical className="w-4 h-4" /></span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{item.text}</div>
                <div className="text-xs text-gray-500 truncate">{item.href}</div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => startEdit(index)} className="px-2 py-1 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Upravit</button>
              <button onClick={() => deleteItem(index)} className="px-2 py-1 text-sm rounded-md bg-red-100 text-red-700 hover:bg-red-200">Smazat</button>
            </div>
          </li>
        ))}
        {editingIndex !== null && (
          <li className="py-3">
            <div className="space-y-2">
              <div>
                <label className="text-sm font-medium">Text</label>
                <input
                  type="text"
                  value={editingItem.text}
                  onChange={(e) => setEditingItem((prev) => ({ ...prev, text: e.target.value }))}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Odkaz</label>
                <input
                  type="text"
                  placeholder="#about nebo https://example.com"
                  value={editingItem.href}
                  onChange={(e) => setEditingItem((prev) => ({ ...prev, href: e.target.value }))}
                />
              </div>
              <div className="flex items-center gap-2">
                <button onClick={applyEdit} className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Uložit</button>
                <button onClick={cancelEdit} className="px-3 py-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200">Zrušit</button>
              </div>
            </div>
          </li>
        )}
      </ul>

      <div className="pt-2 border-t">
        <button onClick={saveChanges} className="px-4 py-2 text-sm rounded-md bg-green-600 text-white hover:bg-green-700">Uložit změny</button>
      </div>

      {/* Modal pro přidání nové položky */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)}>
        <div className="space-y-4">
          <h4 className="text-lg font-semibold">Přidat položku do navigace</h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium">Text</label>
              <input
                type="text"
                value={newItem.text}
                onChange={(e) => setNewItem((prev) => ({ ...prev, text: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-sm font-medium">Typ odkazu</label>
              <select value={newItemType} onChange={(e) => setNewItemType(e.target.value)}>
                <option value="anchor">Sekce (#anchor)</option>
                <option value="url">URL</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Odkaz</label>
              <input
                type="text"
                placeholder={newItemType === 'anchor' ? '#about / #vehicles / #members' : 'https://example.com'}
                value={newItem.href}
                onChange={(e) => setNewItem((prev) => ({ ...prev, href: e.target.value }))}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-gray-500">Rychlé volby:</span>
            <button type="button" onClick={() => setAnchor('#about')} className="px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200">#about</button>
            <button type="button" onClick={() => setAnchor('#vehicles')} className="px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200">#vehicles</button>
            <button type="button" onClick={() => setAnchor('#members')} className="px-2 py-1 text-xs rounded-md bg-gray-100 hover:bg-gray-200">#members</button>
          </div>
          <div className="flex items-center gap-2 justify-end">
            <button onClick={() => setIsAddModalOpen(false)} className="px-3 py-2 text-sm rounded-md bg-gray-100 hover:bg-gray-200">Zrušit</button>
            <button onClick={addNewItem} className="px-3 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Přidat</button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default NavMenuForm;